import { 
  User, 
  UserAccess, 
  UserAccessWithUser, 
  UserAccessCreate, 
  UserAccessUpdate, 
  BulkAccessCreate,
  AccessSummary,
  UserAccessSummary,
  MesttiOpsAccess,
  MyMesttiOpsAccess,
} from '../types/access';
import { apiService } from '../features/shared/service/api';

class AccessService {
  // ===== USUÁRIOS =====
  
  async getCurrentUser(): Promise<User> {
    try {
      const apiUser = await apiService.getUser('me');
      return {
        id: apiUser.id,
        email: apiUser.email,
        full_name: apiUser.full_name,
        user_level: (apiUser as any).user_level || 'USER',
        is_active: apiUser.is_active,
        last_login_at: (apiUser as any).last_login_at,
        created_at: apiUser.created_at,
        updated_at: apiUser.updated_at
      };
    } catch (error) {
      throw new Error('Falha ao obter usuário atual');
    }
  }

  async getUsers(page: number = 1, limit: number = 100): Promise<{ users: User[], total: number, total_pages: number }> {
    try {
      const skip = (page - 1) * limit;
      const response = await apiService.getUsers(skip, limit);
      const users: User[] = response.users.map((apiUser: any) => ({
        id: apiUser.id,
        email: apiUser.email,
        full_name: apiUser.full_name,
        user_level: apiUser.user_level || 'USER',
        is_active: apiUser.is_active,
        last_login_at: apiUser.last_login_at,
        created_at: apiUser.created_at,
        updated_at: apiUser.updated_at
      }));
      
      return {
        users,
        total: response.total,
        total_pages: response.total_pages
      };
    } catch (error) {
      throw new Error('Falha ao obter usuários');
    }
  }

  async getUserById(userId: string): Promise<User> {
    try {
      const apiUser = await apiService.getUser(userId);
      return {
        id: apiUser.id,
        email: apiUser.email,
        full_name: apiUser.full_name,
        user_level: (apiUser as any).user_level || 'USER',
        is_active: apiUser.is_active,
        last_login_at: (apiUser as any).last_login_at,
        created_at: apiUser.created_at,
        updated_at: apiUser.updated_at
      };
    } catch (error) {
      throw new Error('Falha ao obter usuário');
    }
  }

  async createUser(userData: { email: string; full_name: string; password: string; user_level: string }): Promise<User> {
    try {
      const apiUser = await apiService.createUser({
        ...userData,
        is_active: true
      });
      
      return {
        id: apiUser.id,
        email: apiUser.email,
        full_name: apiUser.full_name,
        user_level: (apiUser as any).user_level || 'USER',
        is_active: apiUser.is_active,
        last_login_at: (apiUser as any).last_login_at,
        created_at: apiUser.created_at,
        updated_at: apiUser.updated_at
      };
    } catch (error) {
      throw new Error('Falha ao criar usuário');
    }
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<User> {
    try {
      const apiUser = await apiService.updateUser(userId, userData);
      return {
        id: apiUser.id,
        email: apiUser.email,
        full_name: apiUser.full_name,
        user_level: (apiUser as any).user_level || 'USER',
        is_active: apiUser.is_active,
        last_login_at: (apiUser as any).last_login_at,
        created_at: apiUser.created_at,
        updated_at: apiUser.updated_at
      };
    } catch (error) {
      throw new Error('Falha ao atualizar usuário');
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      await apiService.deleteUser(userId);
    } catch (error) {
      throw new Error('Falha ao deletar usuário');
    }
  }

  // ===== ACESSOS =====

  async getUserAccesses(userId: string): Promise<UserAccess[]> {
    try {
      // console.log('🔐 AccessService - getUserAccesses called for userId:', userId);
      const result = await apiService.getUserAccesses(userId);
      // console.log('🔐 AccessService - getUserAccesses result:', result);
      return result;
    } catch (error) {
      console.error('❌ AccessService - Erro ao obter acessos do usuário:', error);
      throw new Error('Falha ao obter acessos do usuário');
    }
  }

  // ===== MESTTI OPS =====

  getMyMesttiOpsAccess(): Promise<MyMesttiOpsAccess> {
    return apiService.getMyMesttiOpsAccess();
  }

  getMesttiOpsAccess(userId: string): Promise<MesttiOpsAccess> {
    return apiService.getMesttiOpsAccess(userId);
  }

  setMesttiOpsAccess(userId: string, enabled: boolean | null): Promise<MesttiOpsAccess> {
    return apiService.setMesttiOpsAccess(userId, enabled);
  }

  async getAllAccesses(page: number = 1, limit: number = 100): Promise<{ accesses: UserAccessWithUser[], total: number, total_pages: number }> {
    try {
      // Como não temos uma rota para listar todos os acessos, vamos buscar por usuários
      const usersResponse = await this.getUsers(page, limit);
      const accesses: UserAccessWithUser[] = [];
      
      for (const user of usersResponse.users) {
        try {
          const userAccesses = await this.getUserAccesses(user.id);
          accesses.push(...userAccesses.map(access => ({
            ...access,
            user_email: user.email,
            user_full_name: user.full_name
          })));
        } catch (error) {
          console.warn(`Erro ao buscar acessos do usuário ${user.id}:`, error);
        }
      }
      
      return {
        accesses,
        total: accesses.length,
        total_pages: Math.ceil(accesses.length / limit)
      };
    } catch (error) {
      throw new Error('Falha ao obter acessos');
    }
  }

  async getAccessById(accessId: string): Promise<UserAccessWithUser> {
    try {
      // Como não temos uma rota direta para buscar acesso por ID, vamos buscar em todos os usuários
      const usersResponse = await this.getUsers(1, 1000); // Buscar todos os usuários
      
      for (const user of usersResponse.users) {
        try {
          const userAccesses = await this.getUserAccesses(user.id);
          const access = userAccesses.find(a => a.id === accessId);
          if (access) {
            return {
              ...access,
              user_email: user.email,
              user_full_name: user.full_name
            };
          }
        } catch (error) {
          console.warn(`Erro ao buscar acessos do usuário ${user.id}:`, error);
        }
      }
      
      throw new Error('Acesso não encontrado');
    } catch (error) {
      throw new Error('Falha ao obter acesso');
    }
  }

  async createAccess(accessData: UserAccessCreate): Promise<UserAccess> {
    try {
      return await apiService.createUserAccess(accessData.user_id, {
        access_type: accessData.access_type,
        is_active: accessData.is_active
      });
    } catch (error) {
      throw new Error('Falha ao criar acesso');
    }
  }

  async updateAccess(accessId: string, accessData: UserAccessUpdate): Promise<UserAccess> {
    try {
      return await apiService.updateUserAccess(accessId, {
        access_type: accessData.access_type,
        is_active: accessData.is_active
      });
    } catch (error) {
      throw new Error('Falha ao atualizar acesso');
    }
  }

  async deleteAccess(accessId: string): Promise<void> {
    try {
      await apiService.deleteUserAccess(accessId);
    } catch (error) {
      throw new Error('Falha ao deletar acesso');
    }
  }

  // ===== ACESSOS EM LOTE =====

  async createBulkAccess(bulkData: BulkAccessCreate): Promise<UserAccess[]> {
    try {
      const createdAccesses: UserAccess[] = [];
      
      // Assumindo que BulkAccessCreate tem uma propriedade 'accesses'
      const accessList = (bulkData as any).accesses || [];
      
      for (const accessData of accessList) {
        try {
          const access = await this.createAccess(accessData);
          createdAccesses.push(access);
        } catch (error) {
          console.warn(`Erro ao criar acesso para usuário ${accessData.user_id}:`, error);
        }
      }
      
      return createdAccesses;
    } catch (error) {
      throw new Error('Falha ao criar acessos em lote');
    }
  }

  // ===== RELATÓRIOS =====

  async getAccessSummary(): Promise<AccessSummary[]> {
    try {
      const usersResponse = await this.getUsers(1, 1000);
      const accessTypeMap: { [key: string]: { active_count: number; total_count: number; users_with_access: string[] } } = {};
      
      for (const user of usersResponse.users) {
        try {
          const userAccesses = await this.getUserAccesses(user.id);
          
          for (const access of userAccesses) {
            const accessType = access.access_type;
            if (!accessTypeMap[accessType]) {
              accessTypeMap[accessType] = { active_count: 0, total_count: 0, users_with_access: [] };
            }
            
            const summary = accessTypeMap[accessType];
            summary.total_count++;
            if (access.is_active) {
              summary.active_count++;
              summary.users_with_access.push(user.id);
            }
          }
        } catch (error) {
          console.warn(`Erro ao buscar acessos do usuário ${user.id}:`, error);
        }
      }
      
      const summary: AccessSummary[] = [];
      for (const accessType in accessTypeMap) {
        const data = accessTypeMap[accessType];
        summary.push({
          access_type: accessType as any, // Converter para AccessType
          active_count: data.active_count,
          total_count: data.total_count,
          users_with_access: data.users_with_access
        });
      }
      
      return summary;
    } catch (error) {
      throw new Error('Falha ao obter resumo de acessos');
    }
  }

  async getUserAccessSummary(userId: string): Promise<UserAccessSummary> {
    try {
      const user = await this.getUserById(userId);
      const userAccesses = await this.getUserAccesses(userId);
      const activeAccesses = userAccesses.filter(access => access.is_active);
      
      return {
        user_id: user.id,
        user_email: user.email,
        user_full_name: user.full_name,
        user_level: user.user_level as any, // Converter para string
        total_accesses: userAccesses.length,
        active_accesses: activeAccesses.length,
        access_types: activeAccesses.map(access => access.access_type),
        last_granted: new Date().toISOString()
      };
    } catch (error) {
      throw new Error('Falha ao obter resumo de acessos do usuário');
    }
  }

  // ===== UTILITÁRIOS =====

  async checkUserPermission(userId: string, accessType: string): Promise<boolean> {
    try {
      const accesses = await this.getUserAccesses(userId);
      return accesses.some(access => 
        access.access_type === accessType && access.is_active
      );
    } catch (error) {
      console.error('Erro ao verificar permissão:', error);
      return false;
    }
  }

  async getUserActiveAccesses(userId: string): Promise<string[]> {
    try {
      const accesses = await this.getUserAccesses(userId);
      return accesses
        .filter(access => access.is_active)
        .map(access => access.access_type);
    } catch (error) {
      console.error('Erro ao obter acessos ativos:', error);
      return [];
    }
  }
}

export const accessService = new AccessService();
export default accessService;
